/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  Icons,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { getEffectiveLocale } from '../../../../util/genericHelpers';
import LocaleMenuModal from '../../../../components/LocaleMenu/LocaleMenu';
import AuthNavLink from '../AuthNavLink';
import { useDispatch } from 'react-redux';
import { updateProfile } from '../../../ProfileSettingsPage/ProfileSettingsPage.duck';
import {
  normalizeLocale,
  readLocaleFromLocalStorage,
  writeLocaleToLocalStorage,
} from '../GuestLanguageSelector';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import {
  ACTIVE_ROLES,
  getActiveRoleLabel,
  getActiveRoleUpdatePayload,
  getOppositeActiveRole,
  getPostSwitchRoute,
  hasHostSetup,
  isActiveRoleHost,
  resolveActiveRole,
} from '../../../../util/activeRole';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { text, type, href, route } = linkConfig;

  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }

  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    authStateReady,
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount = 0,
    customLinks = [],
    onLogout,
    intl,
    onManageDisableScrolling,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const activeRole = resolveActiveRole(currentUser);
  const isHostMode = isActiveRoleHost(activeRole);
  const nextRole = getOppositeActiveRole(activeRole);
  const switchLabelId =
    nextRole === ACTIVE_ROLES.HOST
      ? 'TopbarDesktop.switchToHost'
      : 'TopbarDesktop.switchToCreative';
  const roleScopedCustomLinks = isHostMode
    ? customLinks
    : customLinks.filter(linkConfig => {
        const routeName = linkConfig?.route?.name;
        const text = String(linkConfig?.text || '').toLowerCase();
        return (
          routeName !== 'NewListingPage' &&
          routeName !== 'EditListingPage' &&
          text !== 'post a new listing'
        );
      });

  const extraLinks = roleScopedCustomLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // ✅ safe fallback (important for Modal)
  const onManageDisableScrollingSafe = onManageDisableScrolling || (() => null);
  const [isLocaleOpen, setIsLocaleOpen] = useState(false);
  const [roleSwitchInProgress, setRoleSwitchInProgress] = useState(false);
  const [roleSwitchError, setRoleSwitchError] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const currentLocale = useMemo(() => {
    const stored = readLocaleFromLocalStorage();
    if (stored) return stored;
    return currentUser?.id ? normalizeLocale(getEffectiveLocale(currentUser)) : 'en';
  }, [currentUser?.id]);

  const handleSelectLocale = nextLocale => {
    const normalized = normalizeLocale(nextLocale);

    try {
      // IntlFromUser applies the locale immediately and syncs it to the user profile.
      writeLocaleToLocalStorage(normalized);
      setIsLocaleOpen(false);
    } catch (e) {
      console.error('Failed to change locale', e);
      setIsLocaleOpen(false);
    }
  };

  const handleRoleSwitch = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (roleSwitchInProgress || !currentUser?.id) {
      return;
    }

    setRoleSwitchError(false);
    setRoleSwitchInProgress(true);

    try {
      const route = getPostSwitchRoute({
        nextRole,
        currentUser,
        currentUserHasListings,
      });
      const updatedUser = await dispatch(
        updateProfile(getActiveRoleUpdatePayload(currentUser, nextRole))
      );

      if (!updatedUser?.id) {
        throw new Error('Failed to persist active role');
      }

      history.push(createResourceLocatorString(route.name, routeConfiguration, route.params, {}));
    } catch (e) {
      console.error('Failed to switch role', e);
      setRoleSwitchError(true);
    } finally {
      setRoleSwitchInProgress(false);
    }
  };

  const languageSelector = (
    <>
      <InlineTextButton
        rootClassName={css.navigationLink}
        onClick={() => setIsLocaleOpen(true)}
        data-testid="mobile-language-menu-trigger"
      >
        <Icons name="globe" />
        <FormattedMessage id="TopbarDesktop.selectLanguage" defaultMessage="Select Language" />
      </InlineTextButton>

      <LocaleMenuModal
        isOpen={isLocaleOpen}
        onClose={() => setIsLocaleOpen(false)}
        currentLocale={currentLocale}
        onSelectLocale={handleSelectLocale}
        title={intl.formatMessage({
          id: 'TopbarDesktop.selectLanguage',
          defaultMessage: 'Select language',
        })}
        onManageDisableScrolling={onManageDisableScrollingSafe}
      />
    </>
  );

  if (!authStateReady) {
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.spacer} />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const signup = (
      <AuthNavLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </AuthNavLink>
    );

    const login = (
      <AuthNavLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </AuthNavLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );

    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <div className={css.customLinksWrapper}>{languageSelector}</div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  if (!currentUser?.id) {
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.spacer} />
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const inboxTab = isHostMode ? 'sales' : 'orders';
  const hostSetupComplete = hasHostSetup({ currentUser });
  const hostScopedRoute = isHostMode && !hostSetupComplete ? 'HostOnboardingPage' : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />

      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>

        <span className={css.activeRolePill}>{getActiveRoleLabel(activeRole)}</span>

        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <Icons name="message" />
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>

          {!isHostMode ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('FavoriteListingsPage'))}
              name="FavoriteListingsPage"
            >
              <Icons name="unfavorite" />
              <FormattedMessage id="TopbarDesktop.favoriteListingsPageLink" />
            </NamedLink>
          ) : null}

          {isHostMode ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('DashboardPage'))}
              name={hostScopedRoute || 'DashboardPage'}
            >
              <Icons name="dashboard" />
              <FormattedMessage id="TopbarDesktop.dashboardPageLink" />
            </NamedLink>
          ) : null}

          {isHostMode ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <Icons name="listings" />
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          ) : null}

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <Icons name="profile" />
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <Icons name="settings" />
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>

          {languageSelector}

          <InlineTextButton
            rootClassName={classNames(css.navigationLink, css.roleSwitchLink)}
            onClick={handleRoleSwitch}
            inProgress={roleSwitchInProgress}
            disabled={roleSwitchInProgress}
          >
            <Icons name="switchRole" />
            <FormattedMessage id={switchLabelId} />
          </InlineTextButton>
          {roleSwitchError ? (
            <div className={css.roleSwitchError}>
              <FormattedMessage id="TopbarDesktop.switchRoleFailed" />
            </div>
          ) : null}
        </div>

        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>

      {isHostMode ? (
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      ) : null}
    </div>
  );
};

export default TopbarMobileMenu;
